import {
  type SECRGrandTotals,
  type SECRReportResult,
  type SECRReportResultCategories,
} from '@shared/api/types';
import { formatNumber } from '@shared/lib/utils';
import { format, parseISO } from 'date-fns';
import { EMISSION_SCOPES } from './constants';

export type ResultsMetric = 'energy' | 'emissions';

export interface SectionContent {
  title: string;
  html: string;
  text: string;
}

export interface ReportSections {
  // When hasPriorYear is true, two separate sections are populated;
  // when false, a single `combined` section replaces them.
  energy: SectionContent | null;
  emissions: SectionContent | null;
  combined: SectionContent | null;
  intensity: SectionContent | null;
  methodology: SectionContent;
  actions: SectionContent | null;
}

const fmt = (n: number | null | undefined) =>
  n != null && !isNaN(n) ? formatNumber(n) : '—';

const fmtIntensity = (n: number | null | undefined) => {
  if (n == null || isNaN(n)) return '—';
  const digits = n > 0 && n < 0.01 ? 4 : 2;
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

function pctChangeText(
  current: number | null | undefined,
  prior: number | null | undefined
): string {
  if (current == null || prior == null || prior === 0) return '—';
  const change = ((current - prior) / prior) * 100;
  const arrow = change >= 0 ? '↑' : '↓';
  return `${arrow} ${Math.abs(change).toFixed(1)}%`;
}

export function deriveYearLabels(startDate: string) {
  const reportYear = parseInt(startDate.slice(0, 4), 10);
  return {
    reportYear,
    prevYearLabel: `${reportYear - 1}/${String(reportYear).slice(2)}`,
    currentYearLabel: `${reportYear}/${String(reportYear + 1).slice(2)}`,
  };
}

export function formatPeriod(startDate: string, endDate: string) {
  return `${format(parseISO(startDate), 'do MMMM yyyy')} – ${format(parseISO(endDate), 'do MMMM yyyy')}`;
}

export function buildMethodologyText(args: {
  organisationName: string;
  period: string;
  hasPriorYear: boolean;
  intensityLabel: string | null | undefined;
  intensityUnit?: string | null;
}): string {
  const {
    organisationName,
    period,
    hasPriorYear,
    intensityLabel,
    intensityUnit,
  } = args;
  const firstYearClause = hasPriorYear
    ? ''
    : `, representing ${organisationName}'s first reporting year,`;
  const intensityClause = intensityLabel
    ? `The intensity metric is reported per ${intensityLabel.toLowerCase()}${intensityUnit ? ` (${intensityUnit})` : ''}.`
    : 'No intensity metric has been selected for this report.';

  return [
    `This Streamlined Energy and Carbon Report has been prepared in accordance with the UK Government's 2019 Environmental Reporting Guidelines and the GHG Protocol Corporate Standard, using the My Emissions carbon intelligence platform. It covers the period from ${period} across ${organisationName}'s UK operations${firstYearClause} and applies the operational control approach.`,
    `The reported energy consumption and total emissions include direct combustion of gas and fuel on site (Scope 1), purchased energy (location-based) (Scope 2), and reimbursed fuel from business travel in employee-owned vehicles (Scope 3). The emission and kWh conversion factors applied are taken from the UK Government Greenhouse Gas Conversion Factors for Company Reporting, published by the Department for Energy Security and Net Zero (DESNZ) for the reporting period.`,
    `Activity data were sourced from [DESCRIBE YOUR DATA SOURCES — e.g. utility bills, meter readings, reimbursement logs, fleet records].`,
    `${intensityClause} Energy efficiency actions undertaken during the reporting period are set out below.`,
  ].join('\n\n');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function methodologyHtml(text: string): string {
  return text
    .split('\n\n')
    .map((p) => `<p>${escapeHtml(p)}</p>`)
    .join('\n');
}

const TABLE_STYLE =
  'border-collapse:collapse;width:100%;font-family:Arial,sans-serif;font-size:11pt;';
const TH_STYLE =
  'border:1px solid #888;background:#f3f4f6;padding:6px 10px;text-align:left;font-weight:600;';
const TD_STYLE = 'border:1px solid #888;padding:6px 10px;';
const TD_RIGHT = `${TD_STYLE}text-align:right;`;

function getCategoryValue(
  cat:
    | {
        tonnes?: number;
        priorYearTonnes?: number | null;
        kwh?: number | null;
        priorYearKwh?: number | null;
      }
    | undefined,
  metric: ResultsMetric,
  prior: boolean
): number | null | undefined {
  if (!cat) return undefined;
  if (metric === 'energy') return prior ? cat.priorYearKwh : cat.kwh;
  return prior ? cat.priorYearTonnes : cat.tonnes;
}

function getTotalValue(
  totals: SECRGrandTotals,
  metric: ResultsMetric,
  prior: boolean
): number | null | undefined {
  return getCategoryValue(totals, metric, prior);
}

function buildResultsTableHtml(
  metric: ResultsMetric,
  categories: SECRReportResultCategories,
  totals: SECRGrandTotals,
  hasPriorYear: boolean,
  prevYearLabel: string,
  currentYearLabel: string
): string {
  const sourceLabel = metric === 'energy' ? 'Energy source' : 'Emission source';
  const unit = metric === 'energy' ? 'kWh' : 'tCO₂e';

  const headerCells = [`<th style="${TH_STYLE}">${sourceLabel}</th>`];
  if (hasPriorYear) {
    headerCells.push(`<th style="${TH_STYLE}">${prevYearLabel} (${unit})</th>`);
  }
  headerCells.push(
    `<th style="${TH_STYLE}">${currentYearLabel} (${unit})</th>`
  );
  if (hasPriorYear) {
    headerCells.push(`<th style="${TH_STYLE}">% Change</th>`);
  }

  const rows: string[] = [];
  EMISSION_SCOPES.forEach(({ label, rows: scopeRows }) => {
    rows.push(
      `<tr><td colspan="${headerCells.length}" style="${TD_STYLE}background:#f9fafb;font-weight:600;text-transform:uppercase;font-size:9pt;">${escapeHtml(label)}</td></tr>`
    );
    scopeRows.forEach(({ key, label: rowLabel }) => {
      const cat = categories[key];
      const current = getCategoryValue(cat, metric, false);
      const prior = getCategoryValue(cat, metric, true);
      const cells = [`<td style="${TD_STYLE}">${escapeHtml(rowLabel)}</td>`];
      if (hasPriorYear) {
        cells.push(`<td style="${TD_RIGHT}">${fmt(prior)}</td>`);
      }
      cells.push(`<td style="${TD_RIGHT}">${fmt(current)}</td>`);
      if (hasPriorYear) {
        cells.push(
          `<td style="${TD_RIGHT}">${pctChangeText(current, prior)}</td>`
        );
      }
      rows.push(`<tr>${cells.join('')}</tr>`);
    });
  });

  // Totals
  const totalCells = [`<td style="${TD_STYLE}font-weight:600;">Total</td>`];
  const currentTotal = getTotalValue(totals, metric, false);
  const priorTotal = getTotalValue(totals, metric, true);
  if (hasPriorYear) {
    totalCells.push(
      `<td style="${TD_RIGHT}font-weight:600;">${fmt(priorTotal)}</td>`
    );
  }
  totalCells.push(
    `<td style="${TD_RIGHT}font-weight:600;">${fmt(currentTotal)}</td>`
  );
  if (hasPriorYear) {
    totalCells.push(
      `<td style="${TD_RIGHT}font-weight:600;">${pctChangeText(currentTotal, priorTotal)}</td>`
    );
  }
  rows.push(`<tr>${totalCells.join('')}</tr>`);

  return `<table style="${TABLE_STYLE}"><thead><tr>${headerCells.join('')}</tr></thead><tbody>${rows.join('')}</tbody></table>`;
}

function buildCombinedTableHtml(
  categories: SECRReportResultCategories,
  totals: SECRGrandTotals,
  currentYearLabel: string
): string {
  const headerCells = [
    `<th style="${TH_STYLE}">Energy source</th>`,
    `<th style="${TH_STYLE}">Emissions (tCO₂e)</th>`,
    `<th style="${TH_STYLE}">Energy (kWh)</th>`,
  ];

  const rows: string[] = [
    `<tr><td colspan="3" style="${TD_STYLE}font-size:9pt;color:#666;">${escapeHtml(currentYearLabel)}</td></tr>`,
  ];
  EMISSION_SCOPES.forEach(({ label, rows: scopeRows }) => {
    rows.push(
      `<tr><td colspan="3" style="${TD_STYLE}background:#f9fafb;font-weight:600;text-transform:uppercase;font-size:9pt;">${escapeHtml(label)}</td></tr>`
    );
    scopeRows.forEach(({ key, label: rowLabel }) => {
      const cat = categories[key];
      rows.push(
        `<tr>` +
          `<td style="${TD_STYLE}">${escapeHtml(rowLabel)}</td>` +
          `<td style="${TD_RIGHT}">${fmt(cat?.tonnes)}</td>` +
          `<td style="${TD_RIGHT}">${fmt(cat?.kwh)}</td>` +
          `</tr>`
      );
    });
  });

  rows.push(
    `<tr>` +
      `<td style="${TD_STYLE}font-weight:600;">Total</td>` +
      `<td style="${TD_RIGHT}font-weight:600;">${fmt(totals.tonnes)}</td>` +
      `<td style="${TD_RIGHT}font-weight:600;">${fmt(totals.kwh)}</td>` +
      `</tr>`
  );

  return `<table style="${TABLE_STYLE}"><thead><tr>${headerCells.join('')}</tr></thead><tbody>${rows.join('')}</tbody></table>`;
}

function buildIntensityTableHtml(
  result: SECRReportResult,
  prevYearLabel: string,
  currentYearLabel: string
): string {
  const intensity = result.intensity;
  if (!intensity || intensity.value == null) return '';
  const hasPriorYear = intensity.priorYearValue != null;
  const label = intensity.label ?? 'Intensity ratio';
  const unit = intensity.unit ?? '';
  const fullLabel = unit ? `${label} (tCO₂e / ${unit})` : label;

  const headerCells = [`<th style="${TH_STYLE}">Metric</th>`];
  if (hasPriorYear) {
    headerCells.push(`<th style="${TH_STYLE}">${prevYearLabel}</th>`);
  }
  headerCells.push(`<th style="${TH_STYLE}">${currentYearLabel}</th>`);
  if (hasPriorYear) {
    headerCells.push(`<th style="${TH_STYLE}">% Change</th>`);
  }

  const rowCells = [`<td style="${TD_STYLE}">${escapeHtml(fullLabel)}</td>`];
  if (hasPriorYear) {
    rowCells.push(
      `<td style="${TD_RIGHT}">${fmtIntensity(intensity.priorYearValue)}</td>`
    );
  }
  rowCells.push(
    `<td style="${TD_RIGHT}">${fmtIntensity(intensity.value)}</td>`
  );
  if (hasPriorYear) {
    rowCells.push(
      `<td style="${TD_RIGHT}">${pctChangeText(intensity.value, intensity.priorYearValue)}</td>`
    );
  }

  return `<table style="${TABLE_STYLE}"><thead><tr>${headerCells.join('')}</tr></thead><tbody><tr>${rowCells.join('')}</tr></tbody></table>`;
}

function tableToPlainText(html: string): string {
  return html
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/td>/gi, '\t')
    .replace(/<\/th>/gi, '\t')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

/**
 * Convert the limited markdown subset emitted by RichTextEditor (Lexical) to
 * HTML. Supports paragraphs, bullet lists (`- item`), bold (`**`/`__`), and
 * italic (`*`/`_`). Anything richer is preserved as plain text.
 */
function markdownToHtml(md: string): string {
  const blocks = md.replace(/\r\n/g, '\n').split(/\n{2,}/);
  return blocks
    .map((block) => {
      const lines = block.split('\n');
      const isList = lines.every((l) => /^\s*-\s+/.test(l));
      if (isList) {
        const items = lines
          .map((l) => l.replace(/^\s*-\s+/, ''))
          .map((l) => `<li>${formatInline(l)}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }
      return `<p>${lines.map(formatInline).join('<br/>')}</p>`;
    })
    .join('\n');
}

function formatInline(s: string): string {
  return escapeHtml(s)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/(^|[^_])_([^_\s][^_]*?)_(?!_)/g, '$1<em>$2</em>');
}

/**
 * Strip Lexical's markdown markers down to a plain-text paragraph form,
 * suitable for the plain/text clipboard payload.
 */
function markdownToPlainText(md: string): string {
  return md
    .replace(/\r\n/g, '\n')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1$2')
    .replace(/(^|[^_])_([^_\s][^_]*?)_(?!_)/g, '$1$2')
    .replace(/^\s*-\s+/gm, '• ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export async function copyHtmlAndText(
  html: string,
  text: string
): Promise<void> {
  if (typeof ClipboardItem !== 'undefined') {
    const item = new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([text], { type: 'text/plain' }),
    });
    await navigator.clipboard.write([item]);
    return;
  }
  await navigator.clipboard.writeText(text);
}

export function buildSections(args: {
  result: SECRReportResult;
  config: {
    methodology?: string | null;
    actionsTaken?: string | null;
  };
  organisationName: string;
  period: string;
  prevYearLabel: string;
  currentYearLabel: string;
  hasPriorYear: boolean;
}): ReportSections {
  const {
    result,
    config,
    organisationName,
    period,
    prevYearLabel,
    currentYearLabel,
    hasPriorYear,
  } = args;

  const intensityHtml = buildIntensityTableHtml(
    result,
    prevYearLabel,
    currentYearLabel
  );

  const methodologyText =
    config.methodology?.trim() ||
    buildMethodologyText({
      organisationName,
      period,
      hasPriorYear,
      intensityLabel: result.intensity?.label,
      intensityUnit: result.intensity?.unit,
    });

  const intensity =
    intensityHtml.length > 0
      ? {
          title: 'Intensity metric',
          html: `<h2>Intensity metric</h2>${intensityHtml}`,
          text: `Intensity metric\n\n${tableToPlainText(intensityHtml)}`,
        }
      : null;

  const actionsMarkdown = config.actionsTaken?.trim();
  const actions = actionsMarkdown
    ? {
        title: 'Actions taken',
        html: `<h2>Actions taken</h2>${markdownToHtml(actionsMarkdown)}`,
        text: `Actions taken\n\n${markdownToPlainText(actionsMarkdown)}`,
      }
    : null;

  let energy: SectionContent | null = null;
  let emissions: SectionContent | null = null;
  let combined: SectionContent | null = null;

  if (hasPriorYear) {
    const energyHtml = buildResultsTableHtml(
      'energy',
      result.categories,
      result.totals,
      hasPriorYear,
      prevYearLabel,
      currentYearLabel
    );
    const emissionsHtml = buildResultsTableHtml(
      'emissions',
      result.categories,
      result.totals,
      hasPriorYear,
      prevYearLabel,
      currentYearLabel
    );
    energy = {
      title: 'Energy results',
      html: `<h2>Energy results</h2>${energyHtml}`,
      text: `Energy results\n\n${tableToPlainText(energyHtml)}`,
    };
    emissions = {
      title: 'Emissions results',
      html: `<h2>Emissions results</h2>${emissionsHtml}`,
      text: `Emissions results\n\n${tableToPlainText(emissionsHtml)}`,
    };
  } else {
    const combinedHtml = buildCombinedTableHtml(
      result.categories,
      result.totals,
      currentYearLabel
    );
    combined = {
      title: 'Emissions & energy',
      html: `<h2>Emissions & energy</h2>${combinedHtml}`,
      text: `Emissions & energy\n\n${tableToPlainText(combinedHtml)}`,
    };
  }

  return {
    energy,
    emissions,
    combined,
    intensity,
    methodology: {
      title: 'Methodology',
      html: `<h2>Methodology</h2>${methodologyHtml(methodologyText)}`,
      text: `Methodology\n\n${methodologyText}`,
    },
    actions,
  };
}

export function buildFullReportHtml(args: {
  organisationName: string;
  period: string;
  sections: ReportSections;
}): string {
  const { organisationName, period, sections } = args;
  const parts: string[] = [
    `<h1>${escapeHtml(organisationName)} – Streamlined Energy and Carbon Report</h1>`,
    `<p><strong>Reporting period:</strong> ${escapeHtml(period)}</p>`,
  ];
  if (sections.combined) parts.push(sections.combined.html);
  if (sections.energy) parts.push(sections.energy.html);
  if (sections.emissions) parts.push(sections.emissions.html);
  if (sections.intensity) parts.push(sections.intensity.html);
  parts.push(sections.methodology.html);
  if (sections.actions) parts.push(sections.actions.html);

  const body = parts.join('\n\n');
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>SECR Report</title></head><body style="font-family:Arial,sans-serif;font-size:11pt;color:#111;">${body}</body></html>`;
}

export function buildFullReportText(args: {
  organisationName: string;
  period: string;
  sections: ReportSections;
}): string {
  const { organisationName, period, sections } = args;
  return [
    `${organisationName} – Streamlined Energy and Carbon Report`,
    `Reporting period: ${period}`,
    sections.combined?.text,
    sections.energy?.text,
    sections.emissions?.text,
    sections.intensity?.text,
    sections.methodology.text,
    sections.actions?.text,
  ]
    .filter(Boolean)
    .join('\n\n');
}
